import type {Sorting} from 'src/typings';
import {createContext, getContextOrThrow} from 'src/core';
import type {Readable} from 'svelte/store';
import type {FilteredValue, FilterValuesMap} from './types';
import type {FondFieldTypeDefinitions} from 'src/features/record/field-types/types';
import {writable} from 'svelte/store';
import {getContext, hasContext} from 'svelte';

const contextKey = Symbol('search-field-type-filters-context');

export interface SearchFieldTypeFiltersContext {
    fieldTypeDefinitions$: Readable<FondFieldTypeDefinitions | null>;
    filteredValues$: Readable<FilterValuesMap>;
    activeSorting$: Readable<Sorting | null>;
    forcedFilterValues: FilterValuesMap;
    setFieldTypeDefinitions: (fieldTypeDefinitions: FondFieldTypeDefinitions | null) => void;
    setFilteredValue: (filterParam: string, value: FilteredValue) => void;
    setSorting: (sorting: Sorting | null) => void;
    removeFilteredValue: (filterParam: string) => void;
}

export function createSearchFieldTypeFiltersContext(defaultFilterValues: FilterValuesMap, forcedFilterValues: FilterValuesMap, activeSorting: Sorting | null = null): SearchFieldTypeFiltersContext {
    const fieldTypeDefinitions$ = writable<FondFieldTypeDefinitions | null>(null);
    const filteredValues$ = writable<FilterValuesMap>(defaultFilterValues);
    const activeSorting$ = writable<Sorting | null>(activeSorting);

    return createContext<SearchFieldTypeFiltersContext>(contextKey, {
        fieldTypeDefinitions$,
        filteredValues$,
        activeSorting$,
        forcedFilterValues,
        setFieldTypeDefinitions: (newFieldTypeDefinitions) => fieldTypeDefinitions$.set(newFieldTypeDefinitions),
        setFilteredValue: (filterParam, value) => filteredValues$.update((currentFilteredValues) => {
            currentFilteredValues[filterParam] = value;
            return currentFilteredValues;
        }),
        setSorting: (newSorting) => activeSorting$.set(newSorting),
        removeFilteredValue: (filterParam) => filteredValues$.update((currentFilteredValues) => {
            delete currentFilteredValues[filterParam];
            return currentFilteredValues;
        })
    });
}

export function getSearchFieldTypeFiltersContext(): SearchFieldTypeFiltersContext {
    return getContextOrThrow<SearchFieldTypeFiltersContext>(contextKey);
}

export function getSearchFieldTypeFiltersContextIfExists(): SearchFieldTypeFiltersContext | null {
    if (!hasContext(contextKey)) {
        return null;
    }

    return getContext<SearchFieldTypeFiltersContext>(contextKey);
}